/**
 * Conectar o Claude ao AI Hub.
 *
 * Mostra o endereço do servidor MCP, gera o token e explica onde colar as duas
 * coisas. É tela de configuração que se usa uma vez por aparelho — por isso
 * mora numa janela, e não ocupa espaço permanente na lista de páginas.
 *
 * ## O token aparece UMA vez
 *
 * O servidor guarda só o hash. Depois que esta janela fechar, nem ele consegue
 * mostrar o valor de novo — a pessoa gera outro. A tela diz isso **antes** de
 * a pessoa clicar em criar, e não depois, porque depois é tarde.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, SessionError, goToSignIn } from '../api.js';
import { notify } from './Toast.jsx';

const dataCurta = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : null;

export function ConectorDialog({ onClose }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [nome, setNome] = useState('');
  const [criando, setCriando] = useState(false);
  /** O valor em claro, enquanto a janela estiver aberta. */
  const [recemCriado, setRecemCriado] = useState(null);
  /** O registro que veio junto, que traz o endereço próprio deste token. */
  const [criado, setCriado] = useState(null);

  const carregar = useCallback(async () => {
    try {
      setDados(await api.conector.get());
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setErro(err.message);
    }
    return undefined;
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    const aoTeclar = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [onClose]);

  async function copiar(texto, oque) {
    try {
      await navigator.clipboard.writeText(texto);
      notify(`${oque} copiado.`);
    } catch {
      notify('Não consegui copiar. Selecione e copie à mão.', 'erro');
    }
  }

  async function criar(e) {
    e.preventDefault();
    setCriando(true);
    try {
      const r = await api.conector.createToken(nome.trim());
      setRecemCriado(r.token);
      setCriado(r.record);
      setNome('');
      await carregar();
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message, 'erro');
    } finally {
      setCriando(false);
    }
    return undefined;
  }

  async function revogar(id) {
    try {
      await api.conector.revokeToken(id);
      notify('Token revogado. O conector que o usava parou de funcionar.');
      await carregar();
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message, 'erro');
    }
    return undefined;
  }

  const ativos = (dados?.tokens ?? []).filter((t) => !t.revokedAt);

  /**
   * O valor pronto para colar no campo do Claude — com o `Bearer` na frente.
   *
   * O campo lá espera o cabeçalho inteiro, não o token. Mostrar o token puro
   * fazia a pessoa ter de digitar a palavra na hora de colar, e esquecer dela
   * dá um 401 que não diz o que faltou.
   */
  const valorDoCabecalho = recemCriado ? `Bearer ${recemCriado}` : null;

  /**
   * O endereço do conector recém-criado — um por pessoa.
   *
   * Vem do servidor junto com o registro, porque é ele que sabe o `id` da
   * linha e a base pública. Montá-lo aqui duplicaria a regra em dois lugares.
   */
  const urlRecemCriada = criado?.url ?? null;

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="item-panel editor-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="conector-titulo"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="panel-header editor-head">
          <h2 id="conector-titulo" className="panel-title">
            Publicar direto do Claude
          </h2>
          <button className="close-button" type="button" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>

        <div className="panel-body editor-body">
          <p className="conector-lead">
            Instale um conector no seu Claude e peça para ele publicar aqui — sem baixar o artefato
            e arrastar. Funciona no Claude Pro, Max, Team e Enterprise.
          </p>

          {erro && (
            <div className="state-message error" role="alert">
              <p>{erro}</p>
            </div>
          )}

          {/* ── passo 1 ─────────────────────────────────────────
              O token vem ANTES do endereço: cada pessoa tem um endereço
              próprio, derivado do token, e ele só existe depois de criado.
              Ver o cabeçalho de `routes/mcp.js` para o porquê. */}
          <section className="conector-passo">
            <h3>
              <span className="conector-num">1</span> Crie o seu token
            </h3>

            {recemCriado ? (
              <div className="conector-segredo">
                <p>
                  <strong>Copie os dois agora.</strong> O token não aparece de novo — guardamos só
                  uma impressão dele. Se perder, revogue e crie outro.
                </p>

                <label className="conector-campo">
                  <span>Endereço do conector — só seu</span>
                  <span className="site-block-actions">
                    <code className="site-url">{urlRecemCriada ?? '…'}</code>
                    <button
                      type="button"
                      className="button secondary"
                      onClick={() => copiar(urlRecemCriada, 'Endereço')}
                    >
                      Copiar
                    </button>
                  </span>
                </label>

                <label className="conector-campo">
                  <span>
                    Cabeçalho <code>Authorization</code>
                  </span>
                  <span className="site-block-actions">
                    {/* Já com o `Bearer` na frente: é o valor inteiro que o campo
                        do Claude espera. Mostrar o token puro obrigava a pessoa a
                        digitar a palavra na hora de colar — e esquecer dela
                        produz um 401 que não explica nada. */}
                    <code className="site-url">{valorDoCabecalho}</code>
                    <button
                      type="button"
                      className="button primary"
                      onClick={() => copiar(valorDoCabecalho, 'Token')}
                    >
                      Copiar
                    </button>
                  </span>
                </label>
              </div>
            ) : (
              <form className="conector-form" onSubmit={criar}>
                <p className="field-hint">
                  Um token por aparelho ajuda a revogar só o que você perdeu. O valor aparece uma
                  única vez, junto com o endereço do seu conector.
                </p>
                <div className="conector-form-linha">
                  <input
                    className="input"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    placeholder="Claude do meu notebook"
                    maxLength={80}
                    required
                  />
                  <button
                    type="submit"
                    className="button primary"
                    disabled={criando || nome.trim().length < 2}
                  >
                    {criando ? 'Criando…' : 'Criar token'}
                  </button>
                </div>
              </form>
            )}
          </section>

          {/* ── passo 2 ─────────────────────────────────────── */}
          <section className="conector-passo">
            <h3>
              <span className="conector-num">2</span> Instale no Claude
            </h3>
            <p className="field-hint">
              Abra <strong>Configurações → Conectores → Adicionar conector personalizado</strong> e
              cole o <strong>endereço</strong> acima.
            </p>
            <p className="field-hint">
              Em <strong>Authentication</strong>, marque <strong>No sign-in</strong> — é a opção
              para servidor com token em vez de OAuth. Depois, em <strong>Request headers</strong>,
              adicione <code>Authorization</code> e cole o valor que começa com <code>Bearer</code>.
            </p>
          </section>

          {/* ── passo 3 ─────────────────────────────────────── */}
          <section className="conector-passo">
            <h3>
              <span className="conector-num">3</span> Peça ao Claude
            </h3>
            <p className="field-hint">
              Com o artefato aberto: <em>&ldquo;publica isso no AI Hub&rdquo;</em>. Ele cria o
              projeto, envia os arquivos e devolve o endereço.
            </p>
            <p className="field-hint">
              A página nasce <strong>privada</strong>. Tornar pública continua sendo um clique aqui
              no AI Hub — o conector não faz isso, de propósito.
            </p>
          </section>

          {/* ── tokens existentes ───────────────────────────── */}
          {ativos.length > 0 && (
            <section className="conector-passo">
              <h3>Tokens ativos</h3>
              <ul className="conector-tokens">
                {ativos.map((t) => (
                  <li key={t.id}>
                    <span className="conector-token-nome">
                      {t.name}
                      {/* O endereço não é segredo, então dá para mostrá-lo de
                          novo — é o que salva quem perdeu a URL mas ainda tem
                          o token guardado no Claude. */}
                      <code className="conector-token-url">{t.url}</code>
                      <small>
                        criado em {dataCurta(t.createdAt)}
                        {t.lastUsedAt ? ` · usado em ${dataCurta(t.lastUsedAt)}` : ' · nunca usado'}
                      </small>
                    </span>
                    <button type="button" className="button ghost" onClick={() => revogar(t.id)}>
                      Revogar
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
